import React, { useState } from 'react';

interface MediaItem {
  mediaType: string;
  media_url: string;
  resizedImageUrl?: string[];
  srcset?: string;
}

interface CarouselProps {
  mediaItems: MediaItem[];
  caption: string;
  permalink: string;
}

const Carousel: React.FC<CarouselProps> = ({ mediaItems, caption, permalink }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentMedia = mediaItems[currentIndex];
  const isVideo = currentMedia.mediaType === 'VIDEO';
  const mediaUrl = currentMedia.media_url;

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? mediaItems.length - 1 : prevIndex - 1
    );
  };

  // New function to handle dot click
  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative">
      <div className="relative">
        {isVideo ? (
          <video controls className="w-full h-auto">
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={mediaUrl}
            srcSet={currentMedia.resizedImageUrl?.map((url, index) => `${url} ${[480, 768, 1200, 1600][index]}w`).join(', ')}
            sizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1200px, 1600px"
            alt={`carousel-item-${currentIndex}`}
            className="w-full h-auto object-cover"
          />
        )}

        {/* Pagination dots */}
        <div className="absolute bottom-14 left-0 right-0 flex justify-center space-x-2">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-2 w-2 rounded-full border border-gray-400 ${
                index === currentIndex 
                  ? 'bg-white bg-opacity-70' 
                  : 'bg-gray-500 bg-opacity-30'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm font-roboto-condensed truncate">
          <a
            href={permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {caption || "No caption provided"}
          </a>
        </div>
      </div>

      {mediaItems.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[#0c6081] text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#f7b95d] focus:outline-none focus:ring-2 focus:ring-[#f64f40]"
          >
            &#10094;
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#0c6081] text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#f7b95d] focus:outline-none focus:ring-2 focus:ring-[#f64f40]"
          >
            &#10095;
          </button>
        </>
      )}
    </div>
  );
};

export default Carousel;
